
import React, { useState, useEffect, useRef } from 'react';
import './CommandPalette.css';
import { useAppState, TopPage, BusinessTab, StudioTab, OpsTab } from '../../context/AppStateContext';
import { useToast } from '../../context/ToastContext';
import SilverIcon from '../shared/SilverIcon';

interface Action {
  id: string;
  group: string;
  label: string;
  icon: React.ComponentProps<typeof SilverIcon>['name'];
  goto?: { page: TopPage; bTab?: BusinessTab; sTab?: StudioTab; oTab?: OpsTab };
  action?: string;
}

const ACTIONS: Action[] = [
  // Navigate
  { id: 'go-home', group: 'Navigate', label: 'Go to Home', icon: 'home', goto: { page: 'home' } },
  { id: 'go-customers', group: 'Navigate', label: 'Business → Customers', icon: 'customers', goto: { page: 'business', bTab: 'customers' } },
  { id: 'go-calendar', group: 'Navigate', label: 'Business → Calendar', icon: 'calendar', goto: { page: 'business', bTab: 'calendar' } },
  { id: 'go-money', group: 'Navigate', label: 'Business → Money', icon: 'money', goto: { page: 'business', bTab: 'money' } },
  { id: 'go-inbox', group: 'Navigate', label: 'Business → Inbox', icon: 'inbox', goto: { page: 'business', bTab: 'inbox' } },
  { id: 'go-analytics', group: 'Navigate', label: 'Business → Analytics', icon: 'analytics', goto: { page: 'business', bTab: 'analytics' } },
  { id: 'go-ai', group: 'Navigate', label: 'Studio → AI Hub', icon: 'ai', goto: { page: 'studio', sTab: 'ai' } },
  { id: 'go-research', group: 'Navigate', label: 'Studio → Research', icon: 'research', goto: { page: 'studio', sTab: 'research' } },
  { id: 'go-media', group: 'Navigate', label: 'Studio → Media', icon: 'media', goto: { page: 'studio', sTab: 'media' } },
  { id: 'go-visuals', group: 'Navigate', label: 'Studio → Visuals', icon: 'visuals', goto: { page: 'studio', sTab: 'visuals' } },
  { id: 'go-monitor', group: 'Navigate', label: 'Ops → Monitor', icon: 'monitor', goto: { page: 'ops', oTab: 'monitor' } },
  { id: 'go-database', group: 'Navigate', label: 'Ops → Database', icon: 'database', goto: { page: 'ops', oTab: 'database' } },
  // Create
  { id: 'new-invoice', group: 'Create', label: 'Create Invoice', icon: 'money', action: 'new-invoice' },
  { id: 'new-customer', group: 'Create', label: 'Add Customer', icon: 'customers', action: 'new-customer' },
  { id: 'new-event', group: 'Create', label: 'New Calendar Event', icon: 'calendar', action: 'new-event' },
  { id: 'compose-email', group: 'Create', label: 'Compose Email', icon: 'inbox', action: 'compose-email' },
  { id: 'start-call', group: 'Create', label: 'Start Video Call', icon: 'video', action: 'start-call' },
];

interface Props {
  onClose: () => void;
}

export default function CommandPalette({ onClose }: Props) {
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(0);
  const { setAppState } = useAppState();
  const { addToast } = useToast();
  const inputRef = useRef<HTMLInputElement>(null);

  const filtered = ACTIONS.filter(a =>
    a.label.toLowerCase().includes(query.toLowerCase()) ||
    a.group.toLowerCase().includes(query.toLowerCase())
  );

  useEffect(() => { inputRef.current?.focus(); }, []);
  useEffect(() => { setSelected(0); }, [query]);

  const groups = Array.from(new Set(filtered.map(a => a.group)));

  const runAction = (action: Action) => {
    if (action.goto) {
      const g = action.goto;
      setAppState(s => ({
        ...s,
        currentPage: g.page,
        ...(g.bTab ? { businessTab: g.bTab } : {}),
        ...(g.sTab ? { studioTab: g.sTab } : {}),
        ...(g.oTab ? { opsTab: g.oTab } : {}),
      }));
    }
    if (action.action) {
      addToast('info', `Action: ${action.label}`);
    }
    onClose();
  };

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowDown') { e.preventDefault(); setSelected(s => Math.min(s + 1, filtered.length - 1)); }
      if (e.key === 'ArrowUp') { e.preventDefault(); setSelected(s => Math.max(s - 1, 0)); }
      if (e.key === 'Enter') { if (filtered[selected]) runAction(filtered[selected]); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [filtered, selected]);

  let globalIndex = 0;

  return (
    <div className="cp-overlay" onClick={onClose}>
      <div className="cp" onClick={e => e.stopPropagation()}>
        <div className="cp__input-wrap">
          <SilverIcon name="search" size={17} style={{ color: 'var(--color-muted)', flexShrink: 0 }} />
          <input
            ref={inputRef}
            className="cp__input"
            placeholder="Search pages, actions, commands…"
            value={query}
            onChange={e => setQuery(e.target.value)}
          />
          <kbd className="cp__esc">esc</kbd>
        </div>
        <div className="cp__results">
          {filtered.length === 0 && (
            <div className="cp__empty">No results for "{query}"</div>
          )}
          {groups.map(group => (
            <div key={group} className="cp__group">
              <div className="cp__group-label">{group}</div>
              {filtered.filter(a => a.group === group).map(action => {
                const idx = globalIndex++;
                return (
                  <button
                    key={action.id}
                    className={`cp__item${selected === idx ? ' cp__item--selected' : ''}`}
                    onClick={() => runAction(action)}
                    onMouseEnter={() => setSelected(idx)}
                  >
                    <span className="cp__item-icon">
                      <SilverIcon name={action.icon} size={15} />
                    </span>
                    <span className="cp__item-label">{action.label}</span>
                    <span className="cp__item-group">{action.group}</span>
                  </button>
                );
              })}
            </div>
          ))}
        </div>
        <div className="cp__footer">
          <span><kbd>↑↓</kbd> Navigate</span>
          <span><kbd>↵</kbd> Select</span>
          <span><kbd>Esc</kbd> Close</span>
        </div>
      </div>
    </div>
  );
}
